import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { CameraSession } from '../../interaction/camera';
import { CAMERA_READINESS_THRESHOLDS } from '../../platform/config/camera-readiness';
import { syntheticPoseDriverEnabled } from '../../platform/config/diagnostics';
import { createTunablesProvider } from '../../platform/config/tunables-provider';
import {
  createAudioService,
  createSilentAudioEngine,
  createWebAudioEngine,
  duckMusicUnderNarration,
  webAudioAvailable,
} from '../../platform/audio';
import {
  createBrowserTtsNarrationService,
  createPrebakedNarrationService,
} from '../../platform/narration';
import type { SessionLaunchConfig } from '../../shared/types/session-launch';
import { createLocalRepositories } from '../../storage/local';
import type { StorageLike } from '../../storage/local';
import { AppServicesContext, type ActivePlayer, type AppServices } from './services-context';

export interface AppServicesProviderProps {
  children: ReactNode;
  /** Injected by tests; production uses the browser's own storage. */
  storage?: StorageLike | null;
}

function buildServices(storage: StorageLike | null | undefined) {
  const repositories = createLocalRepositories(storage === undefined ? {} : { storage });
  /**
   * One sound owner for the sitting (PS5-2), built locked.
   *
   * A device that cannot make a sound — no Web Audio at all, or a stack that refuses to be built
   * (R1-1) — gets the silent engine rather than a broken one, for the same reason a device with no
   * voice gets a silent narration service: a quiet sitting is a sitting, and a second code path for
   * the quiet case is a path that would drift. The engine is built here — never lazily on first
   * play — so that the thing which has to be released on the way out is created by the same
   * boundary that releases it.
   */
  const engine = webAudioAvailable() ? createWebAudioEngine() : null;
  const audio = createAudioService({ engine: engine ?? createSilentAudioEngine() });
  /**
   * Everything after the engine is built inside a rollback (R2-1).
   *
   * The audio context exists from the line above; if anything below throws — a `speechSynthesis`
   * getter that objects, a camera session that will not construct — the bundle is never returned,
   * never pushed anywhere, and nothing is left holding that context. Rolling back and rethrowing,
   * rather than swallowing: whether a narration or camera constructor failing should also be
   * *survivable* is a wider question than the leak, and not one to answer silently here.
   */
  try {
    return assembleServices(repositories, audio, engine);
  } catch (cause) {
    try {
      audio.dispose();
    } catch {
      // Already failing; the rethrow below is the report.
    }
    throw cause;
  }
}

function assembleServices(
  repositories: ReturnType<typeof createLocalRepositories>,
  audio: ReturnType<typeof createAudioService>,
  engine: ReturnType<typeof createWebAudioEngine>,
) {
  const tts = createBrowserTtsNarrationService();
  /**
   * Which engine says a line, decided once here and per line at the seam (Slice A″, PS5-2).
   *
   * `PrebakedNarrationService` is a second `NarrationService`, not a second seam: everything above
   * still hands it a sentence. It is wired only where the browser can actually make a sound —
   * which is whether an engine was *built*, not whether the constructors existed (R1-1) — because
   * the silent engine would accept every recording and play none of them, and a line that the
   * browser voice *could* have said, silently dropped, is a worse sitting than one that was never
   * baked. Within that, the choice is per line: a line this theme has a recording for comes off the
   * disk, and a line it does not goes to the browser voice with its watchdog intact.
   */
  const spoken = engine === null ? tts : createPrebakedNarrationService({ audio, fallback: tts });
  return {
    repositories,
    /**
     * A parent's delete, bound to the store the repositories already own (BL-003, PS6-3).
     *
     * Wired here rather than reached for, so the parent zone never holds a storage handle.
     */
    eraseEverything: () => repositories.eraseEverything(),
    audio,
    /**
     * Every line spoken anywhere in the app ducks the music, because every line goes through
     * here (PS5-2's "ducking is coordinated at the platform layer"). Wrapping at the wiring
     * point rather than at each caller is what makes that true of the calibration screen and a
     * mid-mission hint alike — and what stops a future caller having to remember.
     */
    narration: duckMusicUnderNarration(spoken, audio),
    tunables: createTunablesProvider(),
    // Built once for the whole sitting: the pose engine stays warm across visits to the
    // readiness screen (D-060(3)), and nothing is acquired until a screen asks it to start.
    camera: new CameraSession({
      sampleIntervalMs: CAMERA_READINESS_THRESHOLDS.sampleIntervalMs,
    }),
  };
}

/** Everything a sitting owns, built together and released together. */
type ServiceBundle = ReturnType<typeof buildServices>;

/**
 * Nothing the app is saying, looking at — or playing — should outlive the app.
 *
 * Disposing the camera is the end of the sitting: tracks stop and the pose engine is released
 * (architecture §23). Sound is held to exactly the same rule, and for the same reason: a loop still
 * playing over a page that has gone is a sound a family has no way to stop.
 */
function disposeServices(bundle: ServiceBundle): void {
  /**
   * Each release is independent (R2-2), the same rule the audio engine's `stop` already follows.
   *
   * These ran as three plain statements, so the first one to object took the other two with it. A
   * `speechSynthesis.cancel()` that throws is an ordinary browser fact — and it used to mean the
   * audio context stayed open **and the camera tracks stayed live**. The camera one is not a
   * comfort feature: architecture §23 is a privacy promise, and a camera that keeps running because
   * a speech engine complained is that promise broken by an unrelated failure.
   */
  const independently = (release: () => void): void => {
    try {
      release();
    } catch {
      // A disposer that objects has already failed; it may not stop the ones after it.
    }
  };

  independently(() => {
    bundle.narration.stop();
  });
  independently(() => {
    bundle.audio.dispose();
  });
  independently(() => {
    bundle.camera.dispose();
  });
}

/** The parts of the context that belong to the shell rather than to the sitting's resources. */
interface ShellState {
  activePlayer: ActivePlayer | null;
  setActivePlayer: (player: ActivePlayer | null) => void;
  sessionLaunchConfig: SessionLaunchConfig | null;
  setSessionLaunchConfig: (config: SessionLaunchConfig | null) => void;
  cameraInputEnabled: boolean;
  setCameraInputEnabled: (enabled: boolean) => void;
}

/**
 * **Acquisition is commit-owned** (R3-1). This is the whole of the lifecycle rule.
 *
 * A physical resource — an `AudioContext`, a media element, a camera track, a pose engine — may
 * only be taken by an owner whose release is guaranteed *at the moment it is taken*. React offers
 * exactly one such moment: the body of a committed effect, whose cleanup React itself promises to
 * run. So the bundle is built here, inside the effect, and released by that effect's own cleanup.
 * Acquisition and disposal are one construct, written together, and no other commit anywhere can
 * be responsible for either.
 *
 * **What this deletes.** Every earlier round built the bundle during *render* and then tried to
 * find someone to release the renders that were thrown away: round 1 swept from the provider's own
 * state, round 2 from a module-level pool. Both were the same bet — that some *later, unrelated*
 * commit would come along and sweep. `App.tsx` is where that bet loses: the error boundary above
 * the provider can hold "Something wobbled" on screen until the family closes the tab, and no later
 * commit is ever coming. The pool, the sweep, and the deferred-disposal microtask that went with
 * them are gone, because a render that is never committed now *acquires nothing at all*.
 *
 * **Children only exist inside the commit that owns their resources.** Nothing is rendered below
 * this point until the bundle exists, which is what makes the guarantee total rather than
 * probabilistic: any component that could possibly reach a service is, by construction, inside a
 * tree whose unmount runs the cleanup that releases it. A child that throws takes the provider
 * down with it — and React runs the cleanup on the way.
 *
 * `StrictMode`'s mount → cleanup → re-mount is no longer a special case either. It builds, releases
 * and rebuilds honestly; the second sitting is a whole sitting, and the first one is fully gone.
 */
function SittingScope({
  storage,
  shell,
  children,
}: {
  storage: StorageLike | null | undefined;
  shell: ShellState;
  children: ReactNode;
}) {
  const [bundle, setBundle] = useState<ServiceBundle | null>(null);

  useEffect(() => {
    const built = buildServices(storage);
    /**
     * `react-hooks/set-state-in-effect` is disabled here deliberately, and only here.
     *
     * The rule is about cascading renders, and it is the right default: state that could be derived
     * during render should be. This is the other kind — an *acquisition*. The whole point of this
     * effect is that the browser resources it takes are released by its own cleanup, which is the
     * only release React guarantees, and telling the tree they exist is what the acquisition is
     * for. It happens once per mount and the cost is one extra render before any child mounts.
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBundle(built);
    return () => {
      disposeServices(built);
    };
    // Storage is wired once for the lifetime of the app; changing it mid-session is not a
    // supported flow and would silently orphan everything already written.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (bundle === null) return null;
  return (
    <LiveSitting bundle={bundle} shell={shell}>
      {children}
    </LiveSitting>
  );
}

/** The committed sitting: the bundle exists, so everything below may use it. */
function LiveSitting({
  bundle,
  shell,
  children,
}: {
  bundle: ServiceBundle;
  shell: ShellState;
  children: ReactNode;
}) {
  // Storage can stop accepting writes part-way through a session. Subscribing keeps the shell
  // honest about that, instead of reporting the status it had at construction time.
  const storageBackend = useSyncExternalStore(
    bundle.repositories.subscribeToBackend,
    () => bundle.repositories.backend,
    () => bundle.repositories.backend,
  );

  /**
   * The synthetic-pose driver, on a loopback origin that asked for it by name (Slice D).
   *
   * Loaded on demand so a family's bundle never contains it, and installed over the camera
   * session's frame seam — the one place frames enter the app — so that every consumer downstream
   * is the shipped one. Nothing renders it and nothing links to it; see
   * `src/platform/config/diagnostics.ts` for why it is gated twice.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (
      !syntheticPoseDriverEnabled({
        hostname: window.location.hostname,
        search: window.location.search,
      })
    ) {
      return;
    }

    let cancelled = false;
    void import('../../interaction/camera/synthetic-pose-driver').then((module) => {
      if (!cancelled) module.installSyntheticPoseDriver(bundle.camera);
    });

    return () => {
      cancelled = true;
    };
  }, [bundle]);

  const value = useMemo<AppServices>(
    () => ({
      ...bundle,
      storageBackend,
      ...shell,
    }),
    [bundle, storageBackend, shell],
  );

  return <AppServicesContext.Provider value={value}>{children}</AppServicesContext.Provider>;
}

export function AppServicesProvider({ children, storage }: AppServicesProviderProps) {
  const [activePlayer, setActivePlayer] = useState<ActivePlayer | null>(null);
  // Session-scoped by construction: React state, never a repository. See `SessionLaunchConfig`.
  const [sessionLaunchConfig, setSessionLaunchConfig] = useState<SessionLaunchConfig | null>(null);
  // Likewise session-scoped, and off until a child has trained. See `AppServices.cameraInputEnabled`.
  const [cameraInputEnabled, setCameraInputEnabled] = useState(false);

  /**
   * The shell's own state, which outlives any one bundle.
   *
   * Held here rather than inside {@link SittingScope} so that rebuilding the resources — which
   * `StrictMode` does on every mount — does not lose the chosen player or the launch config.
   */
  const shell = useMemo<ShellState>(
    () => ({
      activePlayer,
      setActivePlayer,
      sessionLaunchConfig,
      setSessionLaunchConfig,
      cameraInputEnabled,
      setCameraInputEnabled,
    }),
    [activePlayer, sessionLaunchConfig, cameraInputEnabled],
  );

  return (
    <SittingScope storage={storage} shell={shell}>
      {children}
    </SittingScope>
  );
}
